/**
 * SOVRANOVA — DimVeil
 *
 * Battery-friendly "low dark mode" for long hands-off sessions (Nidra, sleep,
 * hypnosis). The screen wake lock keeps the phone from sleeping so audio and
 * timers keep flowing — but a bright screen held on for 30 minutes (or all
 * night) wastes battery and lights up a dark room. After a period of no
 * interaction this fades the screen to near-black (OLED panels draw very
 * little at black); any tap lifts it instantly.
 *
 * DELIBERATE LIMITS:
 * - This is a visual veil only. It never pauses, mutes, or intercepts the
 *   session underneath — audio and state continue exactly as if it weren't
 *   there.
 * - One tap lifts the veil and does nothing else, so a person can never
 *   accidentally press a control they can't see. The Urgent-help pill is one
 *   tap behind the veil, same as everything else.
 */
import { useState, useEffect, useRef } from 'react'

export function DimVeil({ active, idleMs = 30000, hint = 'Screen dimmed to save battery — tap to wake' }) {
  const [dim, setDim] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!active) { setDim(false); return }
    const arm = () => { clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setDim(true), idleMs) }
    const reset = () => { setDim(false); arm() }
    arm()
    window.addEventListener('pointerdown', reset)
    window.addEventListener('keydown', reset)
    return () => {
      clearTimeout(timerRef.current)
      window.removeEventListener('pointerdown', reset)
      window.removeEventListener('keydown', reset)
    }
  }, [active, idleMs])

  if (!active || !dim) return null
  return (
    <div
      onPointerDown={() => setDim(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#000', opacity: 0.96,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        transition: 'opacity 0.8s ease',
        cursor: 'pointer',
      }}
      aria-label="Screen dimmed — tap to wake"
    >
      <p style={{ color: '#4a4438', fontSize: '0.78rem', paddingBottom: '2.2rem', margin: 0, fontFamily: 'inherit' }}>
        {hint}
      </p>
    </div>
  )
}
